// =============================================================================
// features/conversations/components/ScheduleReturnButton.tsx — Botão de
// calendário no cabeçalho da conversa (F33-S08, doc 26 §6.4).
//
// Reutiliza `ScheduleAppointmentDialog` de features/sales (contrato de saída
// do F33-S06) — NÃO recria o formulário de agendamento aqui.
//
// Regras deste slot (maior risco de regressão do produto):
//   - Nenhuma query key de conversations/mensagens é tocada. As únicas
//     queries que este arquivo dispara são NOVAS e ISOLADAS:
//       - useAssignableAgents() → ['agents', 'assignable'] (já existe, usada
//         pelo próprio ScheduleAppointmentDialog e pelo CRM)
//       - useScheduledAppointment(id) → ['conversations','schedule-return',
//         'appointment', id] — chave própria deste slot (ver hooks/), sem
//         relação com nenhuma chave existente de conversations ou de sales.
//     Nenhum useConversationSocket é montado aqui — este componente não fala
//     com o socket da conversa.
//   - Gate de visibilidade: permissão `sales_appointments:write` E flag
//     `sales.appointments.enabled`. Sem qualquer um dos dois, o componente
//     retorna `null` — nenhum nó extra no DOM, header idêntico ao de hoje.
//   - Com os dois OK mas sem lead vinculado à conversa, o botão aparece
//     desabilitado com explicação (title/aria) — não fica escondido, para
//     o atendente entender por que não pode agendar.
// =============================================================================

import * as React from 'react';

import { useAssignableAgents } from '../../../hooks/admin/useAgents';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useAuth } from '../../../lib/auth-store';
import { cn } from '../../../lib/cn';
import { formatOrgDateTimeShort } from '../../../lib/orgDateTime';
import { ScheduleAppointmentDialog } from '../../sales';
import { useScheduledAppointment } from '../hooks/useScheduledAppointment';

const FLAG_KEY = 'sales.appointments.enabled';
const WRITE_PERMISSION = 'sales_appointments:write';

function formatScheduledAt(iso: string): string {
  return formatOrgDateTimeShort(iso);
}

// ─── Ícone de calendário (16x16, mesmo padrão dos outros ícones do header) ───

function CalendarIcon(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-4 h-4"
      aria-hidden="true"
    >
      <rect x="2.5" y="3.5" width="11" height="10" rx="1.5" />
      <path d="M2.5 6.5h11" />
      <path d="M5.5 2v3M10.5 2v3" />
    </svg>
  );
}

// ─── Confirmação inline ───────────────────────────────────────────────────────

interface ConfirmationBannerProps {
  appointmentId: string;
  onDismiss: () => void;
}

function ConfirmationBanner({
  appointmentId,
  onDismiss,
}: ConfirmationBannerProps): React.JSX.Element | null {
  const { appointment, isLoading } = useScheduledAppointment(appointmentId);
  const { agents } = useAssignableAgents();

  // Auto-descarte após 10s — não trava a conversa, só some sozinho.
  React.useEffect(() => {
    const timer = window.setTimeout(onDismiss, 10_000);
    return () => window.clearTimeout(timer);
  }, [onDismiss]);

  if (isLoading || !appointment) return null;

  const agentName =
    agents.find((a) => a.id === appointment.assignedAgentId)?.display_name ??
    'responsável a definir';

  return (
    <div
      role="status"
      className="flex items-center gap-2 px-3 py-2 rounded-sm border-l-[3px] flex-shrink-0"
      style={{
        borderColor: 'var(--success)',
        background: 'var(--success-bg)',
        boxShadow: 'var(--elev-1)',
      }}
    >
      <svg
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="w-3.5 h-3.5 text-success shrink-0"
        aria-hidden="true"
      >
        <path d="M3 8.5l3 3 7-7" />
      </svg>
      <p className="font-sans text-xs text-ink">
        Retorno agendado para{' '}
        <span className="font-semibold">{formatScheduledAt(appointment.scheduledAt)}</span> com{' '}
        <span className="font-semibold">{agentName}</span>.
      </p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Fechar confirmação de agendamento"
        className="ml-1 p-0.5 rounded-xs text-ink-3 hover:text-ink transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/30 shrink-0"
      >
        <svg
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.6}
          className="w-3 h-3"
        >
          <path d="M3 3l10 10M13 3L3 13" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
}

// ─── ScheduleReturnButton ─────────────────────────────────────────────────────

export interface ScheduleReturnButtonProps {
  /** UUID do lead vinculado à conversa — `null` quando ainda não há vínculo. */
  leadId: string | null;
  /** Nome do contato, usado como leadName do diálogo (contexto, não obrigatório). */
  leadName?: string | null;
}

export function ScheduleReturnButton({
  leadId,
  leadName,
}: ScheduleReturnButtonProps): React.JSX.Element | null {
  const { hasPermission } = useAuth();
  const { enabled: flagEnabled, isLoading: flagLoading } = useFeatureFlag(FLAG_KEY);

  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [confirmedAppointmentId, setConfirmedAppointmentId] = React.useState<string | null>(null);

  const canWrite = hasPermission(WRITE_PERMISSION);

  // Sem permissão, ou flag ainda carregando/desligada: nenhum nó extra no
  // DOM — a tela fica byte-a-byte a de hoje (DoD do slot).
  if (!canWrite || flagLoading || !flagEnabled) return null;

  const hasLead = leadId !== null && leadId.length > 0;

  return (
    <div className="flex items-center gap-2 min-w-0">
      {confirmedAppointmentId && (
        <ConfirmationBanner
          appointmentId={confirmedAppointmentId}
          onDismiss={() => setConfirmedAppointmentId(null)}
        />
      )}

      <button
        type="button"
        onClick={() => setIsDialogOpen(true)}
        disabled={!hasLead}
        aria-label="Agendar retorno"
        title={
          hasLead ? 'Agendar retorno' : 'Vincule um lead a esta conversa para agendar um retorno.'
        }
        className={cn(
          'shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-sm',
          'transition-colors duration-fast ease',
          'enabled:hover:bg-surface-hover enabled:cursor-pointer',
          'disabled:opacity-40 disabled:cursor-not-allowed',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/30',
        )}
        style={{ border: '1px solid var(--border-subtle)', color: 'var(--text-3)' }}
      >
        <CalendarIcon />
      </button>

      {isDialogOpen && hasLead && (
        <ScheduleAppointmentDialog
          leadId={leadId}
          {...(leadName ? { leadName } : {})}
          onClose={() => setIsDialogOpen(false)}
          onScheduled={(appointmentId) => {
            setIsDialogOpen(false);
            setConfirmedAppointmentId(appointmentId);
          }}
        />
      )}
    </div>
  );
}
